/* eslint-disable react/no-did-update-set-state */
import JsonSchemaForm from 'react-jsonschema-form';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import styled from 'styled-components';

import Loading from 'ravenjs/lib/Loading';
import { extractAPIConfig, fetchAPIData } from 'ravenjs/utils/apis';
import { getThemeProps } from 'ravenjs/utils/theme';
import { warning } from 'ravenjs/utils/warning';

import FormActions from './FormActions';
import FormDirtyCheck from './FormDirtyCheck';
import customFields from './fields';
import customWidgets from './widgets';

const FormStyled = styled.div`
    position: relative;

    .RavenSelect__clear-indicator,
    .RavenSelect__dropdown-indicator {
        cursor: pointer;
    }

    ${getThemeProps('Form.styles')};
`;

class Form extends Component {
    static propTypes = {
        /**
         * A list of action buttons for the form
         */
        actionsList: PropTypes.arrayOf(
            PropTypes.shape({
                children: PropTypes.node,
                color: PropTypes.string,
                description: PropTypes.node,
                descriptionPosition: PropTypes.oneOf(['top', 'bottom']),
                onClick: PropTypes.func,
                title: PropTypes.node,
                type: PropTypes.string,
            })
        ),
        /**
         * A title for the form actions group
         */
        actionsTitle: PropTypes.string,
        /**
         * Callback fired after the form is submitted.
         *
         * @param {object} resp The api response
         */
        afterSubmit: PropTypes.func,
        /**
         * A list of api configs to call
         */
        apis: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
        /**
         * A base URL for making the RESTful API calls.
         */
        baseUrl: PropTypes.string,
        /**
         * The contents of the Form.
         */
        children: PropTypes.node,
        /**
         * To enable form dirty check operation.
         * If set to 'true', the 'isDirty' flag will be passed in onChange callback params.
         */
        enableDirtyCheck: PropTypes.bool,
        /**
         * Handle form submission through an external source.
         */
        externalSubmission: PropTypes.bool,
        /**
         * Handle multi step form submission.
         */
        multiStepSubmission: PropTypes.bool,
        /**
         * Step id to reset pristine state of form.
         */
        stepId: PropTypes.string,
        /**
         * A set of custom fields for the form.
         */
        fields: PropTypes.object,
        /**
         * The `formContext` which gets passed
         * down to react-jsonschema-form.
         */
        formContext: PropTypes.object,
        /**
         * Initial `formData`
         */
        formData: PropTypes.oneOfType([
            PropTypes.array,
            PropTypes.bool,
            PropTypes.object,
            PropTypes.string,
            PropTypes.number,
        ]),
        /**
         * A map of action handlers used by rendered components.
         */
        handlers: PropTypes.object,
        /**
         * If you would like to hide the default form actions.
         */
        hideActions: PropTypes.bool,
        /**
         * A boolean to show the internal `loading` component.
         */
        loading: PropTypes.bool,
        /**
         * The text to be displayed for the internal `loading` component.
         */
        loadingText: PropTypes.string,
        /**
         * An oAuth token to make API calls.
         */
        oAuthToken: PropTypes.string,
        /**
         * Callback fired when a value changes in the form.
         *
         * @param {Object} form  The form object
         */
        onChange: PropTypes.func,
        /**
         * Callback fired when a form goes out of focus.
         *
         * @param {Object} form  The form object
         */
        onBlur: PropTypes.func,
        /**
         * Callback fired when the form validates and has errors present.
         *
         * @param {Object} errors The form errors
         */
        onError: PropTypes.func,
        /**
         * Callback fired when the form is ready to be submitted.
         *
         * @param {Object} formData The current formData
         */
        onSubmit: PropTypes.func,
        /**
         * If `true`, the form will be marked as read-only.
         */
        readonly: PropTypes.func,
        /**
         * The `schema` for info providing what should the Form render.
         */
        schema: PropTypes.object.isRequired,
        /**
         * Transform the data coming into the form via the API call.
         *
         * @param   {Object} formData The current `formData`
         * @returns {Object}          The transformed `formData`
         */
        transformDataIn: PropTypes.func,
        /**
         * Transform the data going out of the form via the `onSubmit` handler.
         *
         * @returns {Object}  The transformed `formData`
         */
        transformDataOut: PropTypes.func,
        /**
         * The `uiSchema` for determining how the Form should be rendered.
         */
        uiSchema: PropTypes.object,
        /**
         * A set of custom widgets for the form.
         */
        widgets: PropTypes.object,
        useSafeSetState: PropTypes.bool,
    };

    static defaultProps = {
        actionsList: [
            {
                children: 'Submit',
                color: 'error',
                onClick: () => {},
            },
        ],
        actionsTitle: '',
        afterSubmit: null,
        apis: {},
        baseUrl: null,
        children: null,
        enableDirtyCheck: false,
        externalSubmission: false,
        multiStepSubmission: false,
        stepId: null,
        fields: null,
        formContext: {},
        formData: {},
        handlers: null,
        hideActions: false,
        loading: null,
        loadingText: null,
        oAuthToken: null,
        onChange: null,
        onBlur: null,
        onError: null,
        onSubmit: null,
        readonly: null,
        transformDataIn: null,
        transformDataOut: null,
        uiSchema: {},
        widgets: null,
        useSafeSetState: false,
    };

    constructor(props) {
        super(props);
        // Extract the `formData`, incase we need to transform it.
        const { formData, loading, loadingText } = props;
        // Transform the incoming data.
        const data = this.transformData(formData, 'in');
        // Set the state with the updated data.
        this.state = {
            formData: data,
            submittingForm: loading != null ? loading : false,
            submittingFormText: loadingText,
            pristine: true,
            taintedFields: [],
        };

        this.formDirtyCheck = new FormDirtyCheck();
        this.ref = React.createRef();

        this._isMounted = false;
    }

    componentDidMount() {
        this._isMounted = true;
    }

    componentDidUpdate(prevProps, prevState) {
        const { formData: prevFormData, stepId: stepIdPrevProp } = prevProps;
        const { formData, multiStepSubmission, stepId: stepIdProp } = this.props;
        if (!_isEqual(formData, prevFormData)) {
            this.safeSetState(() => ({ formData: this.transformData(formData, 'in') }));
        }

        if (multiStepSubmission) {
            if (stepIdProp !== stepIdPrevProp) {
                setTimeout(() => {
                    this.safeSetState({ pristine: true, taintedFields: [] });
                }, 100);
            }
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    safeSetState = (state, callback) => {
        const { useSafeSetState } = this.props;

        if (useSafeSetState) {
            if (this._isMounted) {
                this.setState(state, callback);
            }
        } else {
            this.setState(state, callback);
        }
    };

    /**
     * Return an updated form context
     *
     * @method getFormContext
     * @return {Object}       The updated formContext
     */
    getFormContext = () => {
        const { handlers, formContext } = this.props;
        const { pristine, taintedFields } = this.state;

        return {
            ...formContext,
            getFormData: this.getFormData,
            handlers,
            updateFormData: this.updateFormData,
            pristine,
            taintedFields,
        };
    };

    /**
     * Get the current formData
     *
     * @method getFormData
     * @return {Object}    The current formData
     */
    getFormData = () => {
        const { formData } = this.state;
        return formData;
    };

    /**
     * Get the Form state
     *
     * @method getForm
     * @return {Object} The current form state
     */
    getFormState = () => {
        return this.ref.current.state;
    };

    /**
     * Get the current form schema
     *
     * @method getFormSchema
     * @return {Object}
     */
    getFormSchema = () => {
        const { schema } = this.props;
        return schema;
    };

    /**
     * Get the current form uiSchema
     *
     * @method getFormUiSchema
     * @return {Object}
     */
    getFormUiSchema = () => {
        const { readonly, uiSchema } = this.props;
        const { formData } = this.state;

        if (typeof readonly === 'function' && readonly(formData)) {
            return {
                ...uiSchema,
                'ui:readonly': true,
            };
        }

        return uiSchema;
    };

    handleOnChange = form => {
        const { enableDirtyCheck, onChange } = this.props;
        const outputForm = { ...form };

        // Performing the dirty only if the enableDirtyCheck flag is true for performance.
        if (enableDirtyCheck) {
            this.formDirtyCheck.onChange(form);
            // Adding isDirty flag in the
            outputForm.isDirty = this.formDirtyCheck.isFormDirty();
        }

        this.safeSetState(
            {
                formData: outputForm.formData,
            },
            () => {
                if (typeof onChange === 'function') {
                    onChange(outputForm);
                }
            }
        );
    };

    handleOnBlur = data => {
        const { taintedFields } = this.state;
        setTimeout(async () => {
            if (!taintedFields.includes(data)) {
                await this.safeSetState({
                    taintedFields: [...taintedFields, data],
                });
            }
        }, 0);
        if (typeof this.props.onBlur === 'function') {
            this.props.onBlur(data);
        }
    };

    handleOnError = errors => {
        const { onError } = this.props;
        // Invoke the `onError` if available.
        typeof onError === 'function' && onError(errors);
    };

    /**
     * Handle the form submission internally, transforming the data if needed.
     *
     * @method handleSubmit
     * @param  {Object}     formProps The Form's onSubmit props
     * @param  {Object}     event
     */
    handleSubmit = (formProps, event) => {
        const { formData } = formProps;
        const { afterSubmit, apis, baseUrl, oAuthToken, onSubmit } = this.props;
        // Transform the data based on the 'out' func.
        const transformedData = this.transformData(formData, 'out');
        // If `onSubmit` exists then invoke it.
        if (typeof onSubmit === 'function') {
            onSubmit({ ...formProps, formData: transformedData }, event);
        }
        // Check to see if we have a list of `apis`
        // and the `transformedData` is not empty.
        if (!_isEmpty(transformedData) && !_isEmpty(apis)) {
            // Display a warning to user if the `oAuthToken` is empty.
            warning(!_isEmpty(oAuthToken), [
                `A <Form /> requires an oAuthToken to submit the requested data.`,
            ]);
            // Set the `loading` to true.
            this.safeSetState(() => ({
                submittingForm: true,
                submittingFormText: 'Submitting...',
            }));
            // Extract the API config from the schema.
            const api = extractAPIConfig(apis, 'onSubmit');
            // Extract the requeset object from API config.
            const request = _get(api, 'request', {});
            // Extract the requested `dataKey` for the final fetched data.
            const dataKey = _get(api, 'dataKey', 'data');
            // Build the axios config
            const config = {
                ...request,
                baseURL: baseUrl || request.baseURL,
                data: transformedData,
                headers: {
                    Authorization: `Bearer ${oAuthToken}`,
                },
            };
            // Check to see if the `oAuthToken` is not empty
            // before we start to make the API call.
            if (!_isEmpty(oAuthToken) && !_isEmpty(config.baseURL)) {
                // Make the API call.
                fetchAPIData(config, { data: dataKey })
                    .then(resp => {
                        setTimeout(() => {
                            this.safeSetState({
                                submittingForm: false,
                                submittingFormText: 'Submitted!',
                            });
                        });
                        // Extract the data from the response.
                        const data = _get(resp, 'data');
                        // If `afterSubmit` exists then invoke it.
                        if (typeof afterSubmit === 'function') {
                            afterSubmit({ ...formProps, formData: transformedData, resp: data });
                        }
                    })
                    .catch(error => {
                        setTimeout(() => {
                            this.safeSetState({
                                submittingForm: false,
                                submittingFormText: 'Error submitting form!',
                            });
                        });
                        // If `afterSubmit` exists then invoke it.
                        if (typeof afterSubmit === 'function') {
                            afterSubmit({ ...formProps, formData: transformedData, error });
                        }
                    });
            } else {
                setTimeout(() => {
                    this.safeSetState({
                        submittingForm: false,
                        submittingFormText: 'Submitted!',
                    });
                }, 1000);
            }
        }
    };

    /**
     * Transform the `formData` if needed
     *
     * @method transformData
     * @param  {Object}      formData The current `formData`
     * @param  {string}      inOut    Transform 'in' or 'out'?
     * @return {Object}               The transformed `formData`
     */
    transformData = (formData, inOut) => {
        const { transformDataIn, transformDataOut } = this.props;
        // Switch based on `inOut` key
        switch (inOut) {
            case 'in': {
                const data =
                    typeof transformDataIn === 'function' ? transformDataIn(formData) : formData;
                return data;
            }
            case 'out': {
                const data =
                    typeof transformDataOut === 'function' ? transformDataOut(formData) : formData;
                return data;
            }
            default: {
                return formData;
            }
        }
    };

    /**
     * Update the formData with the passed in values
     *
     * @method updateFormData
     * @param  {Object}       formData The passed in (updated) formData
     */
    updateFormData = ({ formData }) => {
        !_isEmpty(formData) &&
            this.safeSetState(prevState => ({
                formData: {
                    ...prevState.formData,
                    ...formData,
                },
            }));
    };

    /**
     * Render the current form's children
     *
     * @method renderChildren
     * @return {node|null}
     */
    renderChildren = () => {
        const {
            actionsList,
            actionsTitle,
            children,
            externalSubmission,
            hideActions,
            readonly,
        } = this.props;
        const { formData } = this.state;

        const disabled = Boolean(typeof readonly === 'function' && readonly(formData));

        if (externalSubmission) {
            return (
                <button
                    ref={btn => (this.submitButton = btn)}
                    style={{ display: 'none' }}
                    type="submit"
                    onClick={() => {
                        setTimeout(() => {
                            this.safeSetState({ pristine: false });
                        }, 100);
                    }}
                />
            );
        }

        if (_isEmpty(children)) {
            return (
                <FormActions
                    actions={actionsList}
                    disabled={disabled}
                    hideActions={hideActions}
                    title={actionsTitle}
                />
            );
        }

        return children;
    };

    render() {
        const { formData, submittingForm, submittingFormText } = this.state;
        const {
            children,
            fields,
            loading,
            loadingText,
            onSubmit,
            schema,
            uiSchema,
            widgets,
            ...otherProps
        } = this.props;

        const loadingProps = {
            loading: loading != null ? loading : submittingForm,
            text: loadingText != null && loadingText ? loadingText : submittingFormText,
        };

        const showFormFieldsDescription = _get(schema, 'showFormFieldsDescription', true);
        const FormFieldsDescriptionStyles = _get(schema, 'FormFieldsDescriptionStyles', {});

        return (
            <FormStyled>
                <Loading {...loadingProps} />
                {showFormFieldsDescription && (
                    <customFields.FormDescriptionTextField styles={FormFieldsDescriptionStyles} />
                )}
                <JsonSchemaForm
                    {...otherProps}
                    ArrayFieldTemplate={customFields.ArrayFieldTemplate}
                    FieldTemplate={customFields.FieldTemplate}
                    ObjectFieldTemplate={customFields.ObjectFieldTemplate}
                    fields={{ ...customFields, ...fields }}
                    formContext={this.getFormContext()}
                    formData={formData}
                    onChange={this.handleOnChange}
                    onBlur={this.handleOnBlur}
                    onError={this.handleOnError}
                    onSubmit={this.handleSubmit}
                    schema={this.getFormSchema()}
                    uiSchema={this.getFormUiSchema()}
                    widgets={{ ...customWidgets, ...widgets }}
                    ref={this.ref}
                >
                    {this.renderChildren()}
                </JsonSchemaForm>
            </FormStyled>
        );
    }
}

export default Form;
